import { FC, KeyboardEvent, ReactElement, useRef, useState } from 'react';
import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useCombobox, useMultipleSelection } from 'downshift';
import { isEmpty } from 'ramda';
import { BiSearch, BiX } from 'react-icons/bi';

import { DocumentIcon } from '../../ui/icons/document';
import { FolderIcon } from '../../ui/icons/folder';

export type SearchResultType = 'FILE' | 'DIRECTORY';
export type SearchResultItem = {
  title: string;
  value: string;
  subtitle: string;
  type: SearchResultType;
};
export type AutoCompleteMenuStaticItem = { type: 'FULL_SEARCH_DEEPLINK' };
export type AutoCompleteMenuItem =
  | SearchResultItem
  | AutoCompleteMenuStaticItem;

export type AutoCompleteProps = {
  isLoading: boolean;
  placeholder?: string;
  inputProps?: InputProps;
  items: AutoCompleteMenuItem[];
  onFocus: (inputValue: string) => void;
  onSubmit: (inputValue: string) => void;
  onInputValueChange: (value?: string) => void;
  onSelectItem: (item: AutoCompleteMenuItem, inputValue?: string) => void;
};

const iconMap: Record<SearchResultType, FC> = {
  FILE: DocumentIcon,
  DIRECTORY: FolderIcon,
};

export const AutoComplete = ({
  items,
  onSubmit,
  isLoading,
  onFocus,
  inputProps,
  onSelectItem,
  onInputValueChange,
  placeholder = 'Search...',
  ...rest
}: AutoCompleteProps): ReactElement<AutoCompleteProps> => {
  const menuRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const { getDropdownProps } = useMultipleSelection(rest);
  const {
    isOpen,
    closeMenu,
    getItemProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    setInputValue,
  } = useCombobox({
    items,
    selectedItem: undefined,
    highlightedIndex: undefined,
    onInputValueChange: ({ inputValue }) => {
      setIsTyping(true);
      setInputValue(inputValue || '');
      onInputValueChange(inputValue);
      setIsTyping(false);
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: false,
          };
        case useCombobox.stateChangeTypes.InputFocus: {
          onFocus(state.inputValue);
          return {
            ...changes,
            isOpen: true,
          };
        }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: false,
            inputValue: state.inputValue,
            highlightedIndex: state.highlightedIndex,
          };

        case useCombobox.stateChangeTypes.FunctionSelectItem:
          return {
            ...changes,
            inputValue: state.inputValue,
          };
        default:
          return changes;
      }
    },
    onStateChange: ({ inputValue: _inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            onSelectItem(selectedItem, inputValue);
            handleClearSearchValue();
            closeMenu();
          }
          break;
        default:
          break;
      }
    },
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const isFocusedOnInput = highlightedIndex === -1;
    const { key, currentTarget } = e;

    if (key === 'Enter' && isFocusedOnInput) {
      const value = currentTarget.value;
      if (!isEmpty(value)) {
        onSubmit(value);
        setInputValue('');
        currentTarget.blur();
      }
    }
  };

  const handleClearSearchValue = () => setInputValue('');

  return (
    <Stack data-testid="autocomplete-input-container">
      <Stack isInline>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            color="white"
            variant="filled"
            key={selectedItem}
            placeholder={placeholder}
            backgroundColor="#133659"
            border="1px solid #133659"
            _focus={{
              backgroundColor: '#133659',
              border: '1px solid #556f88',
            }}
            _hover={{ backgroundColor: '#133659' }}
            {...getInputProps({
              ...getDropdownProps({
                ref: menuRef,
              }),
              onKeyDown: handleKeyDown,
            })}
            {...inputProps}
          />
          {isLoading || isTyping ? (
            <InputRightElement
              pointerEvents="none"
              data-testid="input-loading-spinner"
            >
              <Spinner color="white" />
            </InputRightElement>
          ) : (
            <>
              {!isEmpty(inputValue) && (
                <InputRightElement>
                  <IconButton
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    aria-label="Clear input"
                    icon={<BiX fontSize="20" />}
                    data-testid="clear-input-btn"
                    _focus={{ boxShadow: 'none' }}
                    onClick={handleClearSearchValue}
                  />
                </InputRightElement>
              )}
            </>
          )}
        </InputGroup>
      </Stack>
      {!isOpen || isLoading || isEmpty(items) || isEmpty(inputValue) ? (
        <Box display="none" {...getMenuProps()} />
      ) : (
        <Box position="absolute" w="24rem" insetBlockStart="32px" zIndex="1">
          <List
            bg="white"
            boxShadow="lg"
            borderWidth="1px"
            borderRadius="4px"
            borderColor="gray.200"
            {...getMenuProps()}
          >
            {items.map((item, index) => {
              const uniqueKey = item.type === 'FULL_SEARCH_DEEPLINK' 
                ? `${item.type}-${inputValue}` 
                : `${item.type}-${item.value || item.title}`;
              
              return (
                <ListItem
                  py={1}
                  px={2}
                  cursor="pointer"
                  borderBottomWidth="1px"
                  borderBottomColor="gray.50"
                  key={uniqueKey}
                  {...getItemProps({ item, index })}
                  bg={highlightedIndex === index ? 'gray.100' : 'inherit'}
                >
                {item.type === 'FULL_SEARCH_DEEPLINK' ? (
                  <Flex alignItems="center" gridGap="3">
                    <Icon as={BiSearch} fontSize="lg" />
                    <Box alignItems="center">
                      <Text color="gray.700" fontWeight="500">
                        <Text as="span">See all results for</Text>{' '}
                        <Text as="span">"{inputValue}"</Text>
                      </Text>
                    </Box>
                  </Flex>
                ) : (
                  <Flex alignItems="center" gridGap="3">
                    <Icon as={iconMap[item.type]} fontSize="xl" />
                    <Box alignItems="center">
                      <Text color="gray.700" title={item.title}>
                        {item.title}
                      </Text>
                      <Text fontSize="10" color="gray.500">
                        {item.subtitle}
                      </Text>
                    </Box>
                  </Flex>
                )}
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Stack>
  );
};
